/**
 * „In Ihre Kontakte übernehmen?“ (Kundenfeedback 18.10.2026, XII): Ein
 * registrierter Treffer bzw. ein Eintrag aus dem Versicherungsverzeichnis
 * wird – auf Nachfrage – ins eigene Adressbuch übernommen, optional als
 * Favorit mit Standardrolle.
 */
import type { Contact } from "@/services/contactModel";
import type { ContactSearchHit } from "@/utils/contactSearchHits";
import { createContact, createContactFromPortal, emptyContact } from "@/services/contactsApi";
import { setContactFavorite } from "@/services/contactSearchApi";
import { MANDANT_ROLE_KEY, contactTypeForRole } from "./participantDialogModel";

/** Treffer, die noch nicht im eigenen Adressbuch stehen. */
export function canAdoptHit (hit: Pick<ContactSearchHit, "source">): boolean {
  return hit.source === "registered" || hit.source === "insurance";
}

function standardRole (role: string): string | null {
  return role && role !== MANDANT_ROLE_KEY ? role : null;
}

async function createFromHit (hit: ContactSearchHit, role: string): Promise<Contact> {
  const contactType = contactTypeForRole(role);
  const departmentId = hit.portal?.departmentId;
  if (hit.source === "registered" && departmentId != null) {
    return createContactFromPortal({ idLinkedDepartment: departmentId, contactType });
  }
  const ins = hit.insurance;
  return createContact({
    ...emptyContact(),
    contactType,
    companyName: hit.name,
    email: hit.email || null,
    streetNo: ins?.streetNo ?? null,
    zip: ins?.zip ?? null,
    city: ins?.city ?? null,
    phone: ins?.phone ?? null,
  });
}

/** Übernimmt den Treffer ins Adressbuch; asFavorite setzt Favorit + Standardrolle. */
export async function adoptHitAsContact (hit: ContactSearchHit, role: string, asFavorite: boolean): Promise<Contact> {
  const created = await createFromHit(hit, role);
  if (!asFavorite || created?.id == null) { return created; }
  return setContactFavorite(created.id, true, standardRole(role));
}

/** Stern an einem eigenen Kontakt: Favorit an (mit Standardrolle) bzw. aus. */
export function toggleContactFavorite (contact: Contact, role: string): Promise<Contact> {
  if (contact.id == null) { return Promise.reject(new Error("Kontakt ohne ID")); }
  const next = !contact.isFavorite;
  return setContactFavorite(contact.id, next, next ? standardRole(role) : null);
}
